import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import useFetchOptions from "./components/hooks/use-fetch-options.js";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    hrStart: '00',
    hrEnd: '23',
    dtType: { label: "Contínua", value: 'continuous' },
    period: { label: "Todos", value: 'all' },
    capture_places: [],
    searchType: 'pcl'
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    hrStart: "Hora inicial",
    hrEnd: "Hora final",
    period: "Período",
    dtType: "Tipo de pesquisa",
    capture_places: "Pontos de coleta",
    searchTypePcl: "Agrupar por ponto de coleta",
    searchTypeLane: "Agrupar por câmera"
};

const CollectionPointPriority = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const { capturePlacesOptions } = useFetchOptions();

    const [fixedTop, setFixedTop] = useState(null);
    const buttonRef = useRef(null);

    const title = 'Importância do ponto de coleta';
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
                searchTypePcl: filters.searchType === 'pcl',
                searchTypeLane: filters.searchType === 'lane',
                searchType: null
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const dateType = filters.dtType.value === 'continuous' ? 'continuous' : 'normal';
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, 'month');

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!ReportUtils.validateHourOnly(filters.hrStart, filters.hrEnd, setLoading, dateType)) return;
            if (!ReportUtils.requiredFilterValidate(filters.period, "Período", setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.dtType, "Tipo de pesquisa", setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.capture_places, "Pontos de coleta", setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                hrStart: filters.hrStart ?? null,
                hrEnd: filters.hrEnd ?? null,
                dtType: filters.dtType.value ?? 'continuous',
                period: filters.period.value ?? 'all',
                capture_places: filters.capture_places?.map(i => i.value) ?? [],
                searchType: filters.searchType
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
                searchTypePcl: filters.searchType === 'pcl',
                searchTypeLane: filters.searchType === 'lane',
                searchType: null},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/collection_point_priority/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(prev => ({
            ...initialFilters(),
            capture_places: capturePlacesOptions
        }));
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.amount}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.amount === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.amount === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.amount === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={reportData?.amount}/>
                            <Content reportData={reportData} data={reportData?.amount} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default CollectionPointPriority;